import { Icon, Button } from "@legal-500/legal500-design-system";
import type { HierarchicalItem } from "../../types/uiTypes";
import styles from "./Modal.module.css";

type DragConfirmationModalProps<T extends HierarchicalItem> = {
  draggedItem: T;
  targetItem: T;
  singularName: string;
  onClose: () => void;
  onConfirm: () => void;
};

export function DragConfirmationModal<T extends HierarchicalItem>({
  draggedItem,
  targetItem,
  singularName,
  onClose,
  onConfirm,
}: DragConfirmationModalProps<T>) {
  // Recursively count all descendants
  const countDescendants = (item: T): number => {
    if (!item.children || item.children.length === 0) return 0;
    return item.children.reduce((count, child) => {
      return count + 1 + countDescendants(child as T);
    }, 0);
  };

  const descendantCount = countDescendants(draggedItem);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modalContainer} ${styles["modal-medium"]}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Confirm Move</h2>
          <button className={styles.modalCloseButton} onClick={onClose} aria-label="Close">
            <Icon icon="cross" size="small" />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.dragConfirmation}>
            <div className={styles.dragConfirmMessage}>
              <Icon icon="info" size="small" />
              <p>
                Are you sure you want to move <strong>{draggedItem.name}</strong>
                {draggedItem.code && <span className={styles.nodeCode}> ({draggedItem.code})</span>}
                {descendantCount > 0 && (
                  <span>
                    {" "}
                    and {descendantCount} descendant
                    {descendantCount !== 1 ? "s" : ""}
                  </span>
                )}
                ?
              </p>
            </div>

            <div className={styles.dragHierarchyPreview}>
              <div className={styles.dragPreviewSection}>
                <h3 className={styles.dragPreviewTitle}>New Parent</h3>
                <div className={styles.dragPreviewContent}>
                  <div className={styles.dragPreviewItem}>
                    <Icon icon="folder" size="small" />
                    <span className={styles.dragPreviewName}>{targetItem.name}</span>
                    {targetItem.code && (
                      <span className={styles.nodeCode}>({targetItem.code})</span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.dragPreviewArrow}>
                <Icon icon="chevron-right" size="small" />
              </div>

              <div className={styles.dragPreviewSection}>
                <h3 className={styles.dragPreviewTitle}>Item Being Moved</h3>
                <div className={styles.dragPreviewContent}>
                  <div className={styles.dragPreviewItem}>
                    <Icon icon="folder" size="small" />
                    <span className={styles.dragPreviewName}>{draggedItem.name}</span>
                    {draggedItem.code && (
                      <span className={styles.nodeCode}>({draggedItem.code})</span>
                    )}
                  </div>
                  <div className={styles.dragPreviewChildren}>
                    {draggedItem.children && draggedItem.children.length > 0 && (
                      <span className={styles.dragPreviewChildrenNote}>
                        + {draggedItem.children.length} direct child
                        {draggedItem.children.length !== 1 ? "ren" : ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.dragWarning}>
              <Icon icon="exclamation" size="small" />
              <span>
                This will change the hierarchy. The {singularName.toLowerCase()} and all its
                descendants will be moved under the new parent.
              </span>
            </div>
          </div>
        </div>
        <div className={styles.modalFooterButtons}>
          <Button onClick={onClose} variant="outlined" color="default">
            Cancel
          </Button>
          <Button onClick={onConfirm} variant="contained" color="primary">
            Confirm Move
          </Button>
        </div>
      </div>
    </div>
  );
}
